import type { TestAnswerFeedbackSchemaType } from '@vedalib/editor/lib/brand'
import type { ElementValue, FeedbackRTValue, TestFeedbackValue } from '@vedalib/editor/lib/elements'
import { FeedbackMode, SectionTypeEnum } from '@vedalib/editor/lib/elements'
import lodash from 'lodash'
import type { ReactNode } from 'react'
import { useId } from 'react'

import type { ElementFeedbackCss, ElementFontCss } from 'services/Branding/types'
import { BlockMode, PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getBlock, getIsInteractiveMode, getSection } from 'services/Store/Project/selectors'
import { getBlockState as getBlockStatePreview } from 'services/Store/Project/selectors/state/block'
import type { IBlockMode } from 'services/Store/Project/types'

import AnswerFeedback from './AnswerFeedback'
import Feedback from './Feedback/Feedback'
import type { RTFeedbackValue } from './TestFeedback.types'

type TestFeedbackProps = {
  children?: ReactNode
  showAnswerFb?: boolean
  showFeedback?: boolean
  isTrueAnswer?: boolean
  mode?: IBlockMode
  font: ElementFontCss<ElementValue>
  feedbackValue?: TestFeedbackValue
  onChange?: (value: FeedbackRTValue) => void
  rtProps?: RTFeedbackValue
  styles: ElementFeedbackCss<TestAnswerFeedbackSchemaType>
  blockUuid?: string
  label?: string
}

const TestFeedback = ({
  showAnswerFb,
  isTrueAnswer,
  mode,
  font,
  feedbackValue,
  onChange,
  rtProps,
  styles,
  blockUuid,
  showFeedback,
  children,
}: TestFeedbackProps) => {
  const { optionTypeFb, generalTypeFb, answerFeedback } = feedbackValue || {}
  const blockResult = useProjectContext(getBlockStatePreview, blockUuid)?.result
  const feedbackMode = useProjectContext(getSection)?.test?.feedbackMode
  const emptyValues = lodash.every(feedbackValue, (value) => value === undefined)
  const isPreview = useProjectContext(getIsInteractiveMode)
  const block = useProjectContext(getBlock)
  const section = useProjectContext(getSection)
  const isDemo = mode?.previewMode === PreviewMode.demo
  const isTest = section?.type === SectionTypeEnum.test
  const fbType = children ? optionTypeFb : generalTypeFb
  const id = useId()

  const showFinalFb = block?.mode === BlockMode.end || feedbackMode === FeedbackMode.SHOW

  const shownAnswerFb =
    showFinalFb || feedbackMode === FeedbackMode.HIDE_FEEDBACK || isDemo || !isTest

  const showOptionGeneralFb =
    showFinalFb || feedbackMode === FeedbackMode.HIDE_ICON || !isPreview || isDemo || !isTest

  return (
    <div>
      {shownAnswerFb && answerFeedback && children ? (
        <AnswerFeedback
          blockResult={blockResult}
          isTrueAnswer={isTrueAnswer}
          isVisible={showAnswerFb}
          mode={mode}
          styles={styles}
        >
          <div aria-describedby={id}>{children}</div>
        </AnswerFeedback>
      ) : (
        <div aria-describedby={id}>{children}</div>
      )}
      {showOptionGeneralFb && showFeedback && !emptyValues && (
        <Feedback
          blockResult={blockResult}
          font={font}
          id={id}
          isTrueAnswer={isTrueAnswer}
          mode={mode}
          onChange={onChange}
          rtProps={rtProps}
          styles={styles}
          type={children ? 'option' : 'general'}
          typeFb={fbType}
        />
      )}
    </div>
  )
}

export default TestFeedback
